import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCheck,
  faInfoCircle,
  faMemo,
} from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  BlueprintRevealStatus,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  HeadlineM,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Card3DWithFallback } from '@sorare/core/src/components/3d/Card3DWithFallback';
import { cardsPreviewContainerStyle } from '@sorare/core/src/components/bundled/CardsPreviewContainer';
import { Card } from '@sorare/core/src/components/card/Card';
import { ScarcityBackground } from '@sorare/core/src/components/card/ScarcityBackground';
import { TokenMetas } from '@sorare/core/src/components/token/TokenMetas';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { earlyAccessBlogUrl } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardBonus } from '#src/components/card/CardBonus/index.tsx';
import { PriceHistoryTooltip } from '#src/components/price/PriceHistoryTooltip/index.tsx';

import { EligibilityTooltip } from './EligibilityTooltip/index.tsx';
import { StreaksPromotion } from './StreaksPromotion/index.tsx';
import type { CardOverview_anyCard } from './__generated__/index.graphql.ts';

const Leftcolumn = styled(Vertical)`
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  z-index: 0;

  @media ${laptopAndAbove} {
    align-items: center;
    justify-content: center;
  }
`;

const Item = styled(Horizontal)`
  padding: var(--unit) var(--double-unit);
  gap: var(--unit);
  width: 100%;
  @media ${laptopAndAbove} {
    gap: 0;
    flex-direction: column;
  }
`;

const RecentSales = styled(ButtonBase)`
  display: flex;
  align-self: center;
  padding: var(--unit);
  gap: var(--double-unit);
  width: 100%;
  align-items: center;
  justify-content: center;
  border-top: 1px solid rgba(255, 255, 255, 0.11);

  @media ${laptopAndAbove} {
    width: auto;
    margin: 0 auto;
    border-top: none;
  }
`;

const CardWrapper = styled.div`
  width: 100%;
  max-width: 80px;
  aspect-ratio: var(--card-aspect-ratio);
  @media ${laptopAndAbove} {
    max-width: 300px;
    min-height: 0;
  }
`;

const Metas = styled(Vertical)`
  align-items: flex-start;
  @media ${laptopAndAbove} {
    align-items: center;
  }
`;

const StyledSorareLogo = styled(Horizontal)`
  font-weight: var(--t-bold);
`;

const StyledStarBall = styled(StarBall)`
  width: 12px;
  height: 12px;
`;

const Attributes = styled(Horizontal)`
  flex-wrap: wrap;
`;

const AttributeWrapper = styled(Horizontal)`
  border-radius: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.1);
`;

const Row = styled(AttributeWrapper)`
  padding-left: var(--unit);
`;

const Value = styled(AttributeWrapper)`
  padding: var(--half-unit) var(--unit);
`;

const BundlePreview = styled.div`
  display: flex;
  gap: var(--unit);

  @media ${laptopAndAbove} {
    max-width: 500px;
    margin: var(--triple-unit) 0;
    ${cardsPreviewContainerStyle}
  }
`;

export const Attribute = ({
  label,
  value,
}: {
  label: ReactNode;
  value: ReactNode;
}) => {
  const isLaptopAndAbove = useIsLaptop();
  const TextTag = isLaptopAndAbove ? LabelM : LabelS;
  return (
    <Row gap={1}>
      <TextTag>{label}</TextTag>
      <TextTag bold>{value}</TextTag>
    </Row>
  );
};

const CenteredContent = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
  margin: auto 0;
  padding: var(--half-unit) 0;
  isolation: isolate;
`;

export const CardOverview = ({
  cards,
  soldOnPrimary,
  showRecentSales = true,
}: {
  cards: CardOverview_anyCard[];
  soldOnPrimary?: boolean;
  showRecentSales?: boolean;
}) => {
  const { sport } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const hasBlueprintCards = cards.some(
    card =>
      card.blueprint &&
      card.blueprint.revealStatus !== BlueprintRevealStatus.REVEALED
  );

  return (
    <Leftcolumn gap={0}>
      <ScarcityBackground
        rarity={cards[0].rarityTyped}
        editionName={cards?.[0]?.cardEditionName}
      />
      <CenteredContent center={isLaptopAndAbove}>
        <Item>
          {cards.length > 1 ? (
            <BundlePreview>
              {cards.map(card => (
                <Card key={card.slug} width={320} card={card} />
              ))}
            </BundlePreview>
          ) : (
            <CardWrapper>
              <Card3DWithFallback
                card={cards[0]}
                forceDisable3d={!isLaptopAndAbove}
              />
            </CardWrapper>
          )}
          <Metas>
            {cards.length === 1 && cards[0].sport === Sport.FOOTBALL && (
              <>
                {!isLaptopAndAbove && (
                  <div>
                    <HeadlineM>{cards[0].anyPlayer.displayName}</HeadlineM>
                    <BodyS>
                      <TokenMetas card={cards[0]} separator={' \u2013 '} />
                    </BodyS>
                  </div>
                )}
                <Attribute
                  label={
                    <>
                      <FontAwesomeIcon icon={faCheck} />{' '}
                      <FormattedMessage
                        id="CardOverview.playable"
                        defaultMessage="Playable"
                      />
                    </>
                  }
                  value={
                    cards.length === 1 && (
                      <Value>
                        <EligibilityTooltip card={cards[0]} />
                      </Value>
                    )
                  }
                />
              </>
            )}
            {!!soldOnPrimary && !!(cards.length === 1 || isLaptopAndAbove) && (
              <Attributes>
                <Attribute
                  label={
                    <Horizontal>
                      <FormattedMessage {...glossary.soldBy} />
                      <StyledSorareLogo gap={0.5}>
                        <StyledStarBall color="var(--c-black)" />
                        Sorare
                      </StyledSorareLogo>
                    </Horizontal>
                  }
                  value={<CardBonus card={cards[0]} />}
                />
                <Attribute
                  label={
                    <Horizontal>
                      <FormattedMessage
                        id="CardOverview.newCard"
                        defaultMessage="New Card"
                      />
                    </Horizontal>
                  }
                  value={
                    <Value>
                      20 <Lightning />
                    </Value>
                  }
                />
              </Attributes>
            )}
          </Metas>
        </Item>
        {cards.length === 1 && !!showRecentSales && (
          <PriceHistoryTooltip
            card={cards[0]}
            displayTransactionType
            Label={props => (
              <RecentSales {...props}>
                <FontAwesomeIcon icon={faMemo} />
                <LabelM>
                  <FormattedMessage
                    id="CardOverview.recentSales"
                    defaultMessage="View recent sales"
                  />
                </LabelM>
              </RecentSales>
            )}
          />
        )}
        {!!hasBlueprintCards && (
          <Button
            color="transparent"
            size="compact"
            to={earlyAccessBlogUrl[sport]}
          >
            <FormattedMessage
              id="CardOverview.earlyAccessInfo"
              defaultMessage="More information on Early Access cards"
            />
            <FontAwesomeIcon icon={faInfoCircle} />
          </Button>
        )}
        {cards.length === 1 && <StreaksPromotion card={cards[0]} />}
      </CenteredContent>
    </Leftcolumn>
  );
};

CardOverview.fragments = {
  anyCard: gql`
    fragment CardOverview_anyCard on AnyCardInterface {
      slug
      cardEditionName
      rarityTyped
      seasonYear
      singleCivilYear
      anyPlayer {
        slug
        displayName
      }
      sport
      blueprint {
        id
      }
      delayedReveal
      ...Card_anyCard
      ...EligibilityTooltip_anyCard
      ...TokenMetas_anyCard
      ...PriceHistoryTooltip_anyCard
      ...CardBonus_anyCard
      ...Card3DWithFallback_anyCardInterface
      ...StreaksPromotion_anyCardInterface
    }
    ${Card.fragments.anyCard}
    ${EligibilityTooltip.fragments.anyCard}
    ${TokenMetas.fragments.anyCard}
    ${PriceHistoryTooltip.fragments.anyCard}
    ${CardBonus.fragments.anyCard}
    ${Card3DWithFallback.fragments.anyCardInterface}
    ${StreaksPromotion.fragments.anyCardInterface}
  ` as TypedDocumentNode<CardOverview_anyCard>,
};
